import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import cacheManager from './CacheManager';
import errorLogger from './utils/errorLogger';

// Componentes de páginas públicas
import Home from './components/Home';
import About from './components/About';
import Termo from './components/Termo';
import PgCadastroSenha from './pages/PgCadastroSenha';
import PgAutoCadastro from './pages/PgAutoCadastro';
import PgUsuarioExistente from './pages/PgUsuarioExistente';

// Componentes do portal do cliente
import PgLogin from './pages/PgLogin';
import PgDashboard from './pages/PgDashboard';
import PgAgenda from './pages/PgAgenda';
import PgMeusAgendamentos from './pages/PgMeusAgendamentos';
import PgMinhasFaturas from './pages/PgMinhasFaturas';
import PgMeuPerfil from './pages/PgMeuPerfil';
import PgNovoAgendamento from './pages/PgNovoAgendamento';
import PgTermosDeUso from './pages/PgTermosDeUso';
import LayoutMenu from './components/layout/LayoutMenu';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import 'react-toastify/dist/ReactToastify.css';
import './css/cliente.css';
import { ToastContainer } from 'react-toastify';

const App = () => {
  const [session, setSession] = useState(null);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [termoAceito, setTermoAceito] = useState(false);
  const [isCheckingTermo, setIsCheckingTermo] = useState(false);

  useEffect(() => {
    // Log de inicialização do app
    errorLogger.logInfo('App initialized', {
      userAgent: navigator.userAgent,
      screen: { width: window.screen.width, height: window.screen.height },
      viewport: { width: window.innerWidth, height: window.innerHeight },
    });

    // Verificar se há sessão salva no localStorage
    const savedSession = localStorage.getItem('clienteSession');
    if (savedSession) {
      try {
        const sessionData = JSON.parse(savedSession);
        setSession(sessionData);
        cacheManager.setSessionData(sessionData);
        errorLogger.setUserId(sessionData.id_cliente);
        errorLogger.logInfo('Session restored', { userId: sessionData.id_cliente });
        verificarTermoAceito(sessionData.id_cliente);
      } catch (error) {
        console.error('Erro ao recuperar sessão:', error);
        errorLogger.log(error, { context: 'sessionRestore' });
        localStorage.removeItem('clienteSession');
      }
    }
    setIsCheckingSession(false);
  }, []);

  const verificarTermoAceito = async (id_cliente) => {
    if (!id_cliente) return;
    
    setIsCheckingTermo(true);
    try {
      const response = await fetch(`https://api.faeworks.com.br/cliente/read.php?id=${id_cliente}`);
      const data = await response.json();
      
      if (data && data[0]) {
        const cliente = data[0];
        setTermoAceito(!!cliente.contrato_data_assinatura);
      }
    } catch (error) {
      console.error('Erro ao verificar termo:', error);
      errorLogger.log(error, { context: 'verificarTermo', id_cliente });
      setTermoAceito(false);
    } finally {
      setIsCheckingTermo(false);
    }
  };

  const handleLogin = (sessionData) => {
    console.log('Session Data:', sessionData);
    setSession(sessionData);
    cacheManager.setSessionData(sessionData);
    errorLogger.setUserId(sessionData.id_cliente);
    errorLogger.logInfo('User logged in', { userId: sessionData.id_cliente });
    // Salvar sessão no localStorage
    localStorage.setItem('clienteSession', JSON.stringify(sessionData));
    // Verificar se já aceitou o termo
    verificarTermoAceito(sessionData.id_cliente);
  };

  if (isCheckingSession || isCheckingTermo) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div>
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        <Routes>
          {/* Rotas públicas - mantidas para compatibilidade */}
          <Route path="/home" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/termo" element={<Termo />} />
          <Route path="/termo/:id_cliente/:token" element={<Termo />} />
          <Route path="/cadastro-senha/:id/:token" element={<PgCadastroSenha />} />
          <Route path="/cadastro" element={<PgAutoCadastro />} />
          <Route path="/usuario-existente" element={<PgUsuarioExistente />} />

          {/* Rota de login */}
          <Route 
            path="/login" 
            element={
              session ? <Navigate to="/meus-agendamentos" replace /> : <PgLogin onLogin={handleLogin} />
            } 
          />

          {/* Rotas protegidas do portal do cliente */}
          {session ? (
            <>
              {/* Se não aceitou o termo, redireciona para termos-de-uso */}
              {!termoAceito ? (
                <>
                  <Route path="*" element={<Navigate to="/termos-de-uso" replace />} />
                  <Route 
                    path="/termos-de-uso" 
                    element={
                      <>
                        <LayoutMenu clienteNome={session.nome} />
                        <PgTermosDeUso onAccept={() => setTermoAceito(true)} />
                      </>
                    } 
                  />
                </>
              ) : (
                <>
                  <Route path="/" element={<Navigate to="/meus-agendamentos" replace />} />
                  <Route 
                    path="/dashboard" 
                    element={
                      <>
                        <LayoutMenu clienteNome={session.nome} />
                        <PgDashboard />
                      </>
                    } 
                  />
                  <Route 
                    path="/agenda" 
                    element={
                      <>
                        <LayoutMenu clienteNome={session.nome} />
                        <PgAgenda />
                      </>
                    } 
                  />
                  <Route 
                    path="/novo-agendamento" 
                    element={
                      <>
                        <LayoutMenu clienteNome={session.nome} />
                        <PgNovoAgendamento />
                      </>
                    } 
                  />
                  <Route 
                    path="/meus-agendamentos" 
                    element={
                      <>
                        <LayoutMenu clienteNome={session.nome} />
                        <PgMeusAgendamentos />
                      </>
                    } 
                  />
                  <Route 
                    path="/minhas-faturas" 
                    element={
                      <>
                        <LayoutMenu clienteNome={session.nome} />
                        <PgMinhasFaturas />
                      </>
                    } 
                  />
                  <Route 
                    path="/meu-perfil" 
                    element={
                      <>
                        <LayoutMenu clienteNome={session.nome} />
                        <PgMeuPerfil />
                      </>
                    } 
                  />
                  <Route 
                    path="/termos-de-uso" 
                    element={
                      <>
                        <LayoutMenu clienteNome={session.nome} />
                        <PgTermosDeUso onAccept={() => setTermoAceito(true)} />
                      </>
                    } 
                  />
                </>
              )}
            </>
          ) : (
            <Route path="*" element={<Navigate to="/login" replace />} />
          )}
        </Routes>
      </div>
    </Router>
  );
};

export default App;